/**
* Licensed Materials - Property of IBM
* IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2019
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/

define([
	'../../lib/@waca/dashboard-common/dist/core/Model',
	'../../util/DashboardFormatter',
	'../../widgets/livewidget/query/CommonQueryBuilder',
	'underscore'
],
function(Class, Formatter, CommonQueryBuilder, _) {
	'use strict';

	var NonNumericFilterModel = Class.extend({

		whitelistAttrs: ['name', 'selectedMembers', 'inverted', 'condition'],

		init: function() {
			NonNumericFilterModel.inherited('init', this, arguments);
			this.name = this.name || '';
			this.isToggled = Boolean(this.isToggled) || false;
			this.inverted = Boolean(this.inverted) || false;
			this.selectedMembers = {};
			this.condition = null;
			this.filterConditionQueryResults = {};
			this.includedMembers = {};
			this.excludedMembers = {};
			this.deSelectedMemberRowIdx = [];
			this.maxEntriesAllow = CommonQueryBuilder.getQueryResultsSizeLimit();
		},

		setName: function (name) {
			this.name = name;
		},

		getName: function () {
			return this.name;
		},

		getValue: function (rowIdx) {
			var entry = this.list[rowIdx];
			var isSelected = entry.selected || false;
			if (entry) {
				var value = {
					id: rowIdx,
					caption: Formatter.format(entry.d),
					selected: this.inverted ? !isSelected : isSelected
				};
				//Only set the matchesCondition dot if there's a condition && the entry matches the condition (or it doesn't and invert is set)
				this._updateMatchesCondition(value, this.condition &&
												((!this.inverted && entry.matchesCondition) || (this.inverted && !entry.matchesCondition)));

				return value;
			}

			return {};
		},

		toggleSelectState: function (rowIdx) {
			var member = this.list[rowIdx];
			if (!member) {
				throw new Error ('cannot toggleSelectState, member not found');
			}

			if (member.selected) {
				this.excludeMember(member);
				this.removeMemberFromSelectList(member);
			} else {
				this.includeMember(member);
				this.addMemberToSelectList(member);
			}

			this._updateDeSelectMemberRowIdx(member, rowIdx);
		},

		toggleSelectStateAll: function() {
			if (this.list && this.list.length >0) {
				for(var i=0; i<this.list.length; i++) {
					this.toggleSelectState(i);
				}
			}
		},

		/**
		* Cache deselect values while user interacts with React UI before selecting OK or Cancel to close the UI
		* After user clicks OK, these values will be use to remove any entries loaded after max entries allowed. This is to ensure the UI always display max entries allow
		* when there are no filter values beyond maximum entries allow
		*
		* @param {object} member - the member to cache
		* @param {number} rowIdx - the index to this.list where the member locates
		* @param {boolean} [applyInverting] - Optional to indicate this the invert action in the React UI
		*/
		_updateDeSelectMemberRowIdx: function(member, rowIdx, applyInverting) {
			if (this.maxEntriesAllow && rowIdx >= this.maxEntriesAllow) {
				var isSelected = member.selected || false;
				var selected = applyInverting ? (this.inverted ? !isSelected : isSelected) : isSelected;

				if (selected) {
					var idx = this.deSelectedMemberRowIdx.indexOf(rowIdx);
					if (idx !== -1) {
						this.deSelectedMemberRowIdx.splice(idx, 1);
					}
				} else if (this.deSelectedMemberRowIdx.indexOf(rowIdx) === -1) {
					this.deSelectedMemberRowIdx.push(rowIdx);
				}
			}
		},

		/**
		* Calls when user hit the 'Cancel' button
		*/
		onClose: function() {
			this.deSelectedMemberRowIdx.length = 0;
		},

		/**
		* Calls when user hit the 'OK' button
		*/
		onDone: function() {
			if (this.deSelectedMemberRowIdx.length > 0) {
				var beginIdx = 0;
				var members = [];
				//Make sure to write unit test with rowIdx = 0
				_.each(this.deSelectedMemberRowIdx, function(rowIdx) {
					members = members.concat(this.list.slice(beginIdx, rowIdx));
					beginIdx = rowIdx + 1;
				}, this);

				if (beginIdx < this.list.length) {
					members = members.concat(this.list.slice(beginIdx));
				}

				this.set({
					list: members
				});
				this.deSelectedMemberRowIdx.length = 0;
			}
		},

		// SCENARIOS
		// FCQR: [A, B, C, D]
		// INCL: []
		// EXCL: []
		//
		// SCENARIO A:
		// * uncheck B -> EXCL: [B]
		// * recheck B -> remove from EXCL -> don’t add to INCL unless B !in FCQR
		//
		// SCENARIO B:
		// * check E -> INCL: [E]
		// * uncheck E -> remove from INCL -> don’t add to EXCL unless E in FCQR

		excludeMember: function(member) {
		// if member is in included members, remove it from there
			if (this.includedMembers[member.u]) {
				delete this.includedMembers[member.u];
			}

			// only add to excluded members if member is in FCQR
			if (this.filterConditionQueryResults[member.u]) {
				this.excludedMembers[member.u] = member;
			}
		},

		includeMember: function(member) {
		//if member is in excluded members, remove it from there
			if (this.excludedMembers[member.u]) {
				delete this.excludedMembers[member.u];
			}

			// only add to included members if member is not in FCQR
			if (!this.filterConditionQueryResults[member.u]) {
				this.includedMembers[member.u] = member;
			}
		},


		/**
		 * Add the specified member to the member list (eg a user checks a member or checks a member to add it to the condition matches)
		 * @param member - the member to remove from the list (ie: uncheck).
		 */
		addMemberToSelectList: function (member, options = {}) {
			member.selected = true;
			this.selectedMembers[member.u] = member;
			if(!options.silent){
				this.triggerMemberSelected(member);
			}
		},

		triggerMemberSelected: function(member) {
			this.trigger('change:selectedMember', { useValue: member.u, state: member.selected });
		},

		//Similar to a simple setter, except that if the condition is false, matchesCondition is deleted from the member.
		_updateMatchesCondition: function(member, trueState) {
			if (trueState) {
				member.matchesCondition = trueState;
			} else if (_.has(member, 'matchesCondition')) {
				delete member.matchesCondition;
			}
		},

		/**
		 * Remove the specified member from the member list (eg a user unchecks a previously checked or default condition match member)
		 * @param member - the member to remove from the list (ie: uncheck).
		 */
		removeMemberFromSelectList: function (member) {
			member.selected = false;
			delete this.selectedMembers[member.u];
			this.trigger('change:selectedMember', { useValue: member.u, state: member.selected });
		},

		setSelectedMembers: function (filterValues, options) {
			(filterValues || []).forEach(function(fValue) {
				_.find(this.list, function(member) {
					// matching items may have differing use values - if a filter was created with unique
					// facet turned off, its use and display values will be the same and we can also test
					// for matches via the display values
					if ((fValue.u === member.u) || (fValue.u === fValue.d && fValue.d === member.d)) {
						this.addMemberToSelectList(member, options);
						return true;
					}
				}.bind(this));
				if (!this.selectedMembers[fValue.u]) {
					this.addMemberToSelectList(fValue, options);
				}
			}.bind(this));
		},

		getIsToggled: function() {
			return this.isToggled;
		},

		setIsToggled: function(trueOrFalse, options) {
			this.set({ isToggled: trueOrFalse }, options);
		},

		getInvert: function () {
			return this.inverted;
		},

		setInvert: function (trueOrFalse, options) {
			this.set({ inverted: trueOrFalse }, options);
		},

		/**
		* Calls when a user click on the 'Invert' option in React UI
		*/
		applyChangeInvert: function() {
			if (this.list.length > this.maxEntriesAllow) {
				for(var rowIdx = this.maxEntriesAllow; rowIdx < this.list.length; rowIdx++) {
					this._updateDeSelectMemberRowIdx(this.list[rowIdx], rowIdx, true);
				}
			}
		},

		setCondition: function (condition) {
			this.set({ condition: condition });
		},

		/**
		 * mark all members in the selection list that match the condition which means:
		 *	1) mark all condition matches with a green dot
		 *	2) check any boxes as selected UNLESS they are in the user excludeValues list.
		 * @param useValuesMatchingCondition - the useValues for the members that match the condition.
		 */
		applyFilterConditionQueryResults: function (useValuesMatchingCondition) {
			useValuesMatchingCondition.forEach(function(valueId) {
				var matchedMember = _.find(this.list, function(member) {
					return member.u === valueId;
				});

				if (matchedMember) {
					matchedMember.matchesCondition = true;
					if (!_.has(this.excludedMembers, valueId)) {
					//Put a check on any member that matches the condition AND isn't in the exclude list.
						this.addMemberToSelectList(matchedMember, {
							silent: true
						});
					}
				}
			}.bind(this));
		},

		clearCondition: function () {
			this.setCondition(null);
			this.clearMatchesConditionMarkers();

			// keep included members, these become the selected members. If excludedMembers are present, keep them as well
			const excludedMembers = this.excludedMembers ? JSON.parse(JSON.stringify(this.excludedMembers)) : {};
			this.set({ selectedMembers: JSON.parse(JSON.stringify(this.includedMembers)), excludedMembers: excludedMembers });

			// deselect members of this.list that were set by the filter condition,
			// as long as it isn't an explicitly included value
			_.each(this.filterConditionQueryResults, function(filterConditionMember) {
				if (!_.has(this.includedMembers, filterConditionMember.u)) {
					this.removeMemberFromSelectList(_.find(this.list, function(member) {
						return member.u === filterConditionMember.u;
					}));
				}
			}.bind(this));
			this.filterConditionQueryResults = {};
		},

		/**
		 * Clear the matchesCondition markers (green dots)
		 */
		clearMatchesConditionMarkers: function() {
			_.each(this.list, function(listElement) {
				this._updateMatchesCondition(listElement, false);
			}.bind(this));
		},

		/**
		 * Clear the selected checkboxes which include user selections and matched condition defaults.
		 * @param clearInvert (default TRUE) - if specified and FALSE, the state of invert will NOT be cleared
		 */
		clearValues: function (clearInvert, doClearAllAction, options) {
			clearInvert = (clearInvert === undefined) ? true : clearInvert;
			_.each(this.list, function(member, rowIdx) {
				member.selected = false;
				if (doClearAllAction) {
					this._updateDeSelectMemberRowIdx(member, rowIdx);
				}
			}, this);
			if (clearInvert) {
				this.setInvert(false, options);
			}
			this.set({
				selectedMembers: {},
				includedMembers: {}
			}, options);
		},

		/**
		 * Called after the conditions query executes to set the state of checkmarks and matchesCondition markers (green dots)
		 * @param results - query results whose use values are to be marked.
		 */
		setFilterConditionQueryResults: function(results) {
		//Clear the condition markers prior to applying the latest condition.
			this.clearMatchesConditionMarkers();

			_.each(results, function(result) {
				this.filterConditionQueryResults[result.u] = result;

				// if result is not in this.list, add to this.list so setSelectedMembers will work on it
				if (!_.findWhere(this.list, { u: result.u })) {
					this.list.push(result);
				}
			}.bind(this));

			var resultsUseValues = _.pluck(results, 'u');

			this.applyFilterConditionQueryResults(resultsUseValues);
		},

		toJSON: function () {
			var result = NonNumericFilterModel.inherited('toJSON', this, arguments);

			// Provide default values
			result.inverted = this.inverted || false;
			result.isToggled = this.isToggled || false;

			return result;
		},

		setExcludedMembers: function(excludedMembersArray) {
			_.each(excludedMembersArray, function(excludedMember) {
				this.excludedMembers[excludedMember.u] = excludedMember;
			}.bind(this));
		},

		getExcludedMembersArray: function() {
			return _.toArray(this.excludedMembers);
		},

		setIncludedMembers: function(includedMembersArray) {
			_.each(includedMembersArray, function(includedMember) {
				this.includedMembers[includedMember.u] = includedMember;
			}.bind(this));
		},

		getIncludedMembersArray: function() {
			return _.toArray(this.includedMembers);
		},

		remove: function () {
			this.list = null;
			this.selectedMembers = null;
			this.name = null;
			this.deSelectedMemberRowIdx.length = 0;
			this.haveSelecteMembersBeyondMaxEntriesAllow = false;
		}
	});

	return NonNumericFilterModel;
});
